import { FC, useEffect, useRef, useState } from 'react'
import { ReactComponent as CampaignIcon } from 'src/assets/svg/camp-card.svg'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { Skeleton } from 'src/components/Skeleton'
import { Tooltip } from 'src/components/Tooltip'
import { CampaignsOverviewCampaignWinner } from 'src/services/__generated__/api'
import styles from './activeCampaignsCard.module.scss'

// .keyword is min-height 32 / max-width 140 and .badge is 20px tall with a 64px
// radius (activeCampaignsCard.module.scss). Mirrors one .box of the card.
const BoxSkeleton: FC = () => (
  <>
    <Skeleton height={32} width={140} />
    <Skeleton height={20} radius={64} width={56} />
  </>
)

interface TopCampaignCardProps {
  hasData?: boolean
  isLoading?: boolean
  mostNegative?: CampaignsOverviewCampaignWinner
  mostPositive?: CampaignsOverviewCampaignWinner
}

const formatPercentage = (ratio?: number, sign: 1 | -1 = 1): number => {
  if (ratio === undefined || ratio === null) return 0
  return sign * Math.round(ratio * 100)
}

export const TopCampaignCard: FC<TopCampaignCardProps> = ({
  hasData = false,
  isLoading = false,
  mostNegative,
  mostPositive,
}) => {
  const positiveTextRef = useRef<HTMLDivElement>(null)
  const negativeTextRef = useRef<HTMLDivElement>(null)
  const [isPositiveTruncated, setIsPositiveTruncated] = useState(false)
  const [isNegativeTruncated, setIsNegativeTruncated] = useState(false)

  const positiveData =
    hasData && mostPositive
      ? {
          percentage: formatPercentage(mostPositive.positiveRatio, 1),
          text: mostPositive.name || 'Unnamed campaign',
        }
      : null
  const negativeData =
    hasData && mostNegative
      ? {
          percentage: formatPercentage(mostNegative.negativeRatio, -1),
          text: mostNegative.name || 'Unnamed campaign',
        }
      : null

  useEffect(() => {
    const checkTruncation = (element: HTMLDivElement | null) => {
      if (!element) return false
      return element.scrollHeight > element.clientHeight
    }

    setIsPositiveTruncated(checkTruncation(positiveTextRef.current))
    setIsNegativeTruncated(checkTruncation(negativeTextRef.current))
  }, [positiveData?.text, negativeData?.text])

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <CampaignIcon className={styles.icon} />
        <h3 className={styles.title}>Top Campaign</h3>
      </div>
      <div className={styles.container}>
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dot}></span>
            <span className={styles.labelText}>Most Positive</span>
          </div>
          {/* Loading first: with no winner yet, positiveData is null and the
              card used to assert "No Data" for data still in flight. */}
          {isLoading ? (
            <BoxSkeleton />
          ) : positiveData ? (
            <>
              {isPositiveTruncated ? (
                <Tooltip
                  overlay={positiveData.text}
                  overlayClassName="topCampaignTooltip"
                  placement="top"
                >
                  <div className={styles.keyword} ref={positiveTextRef}>
                    {positiveData.text}
                  </div>
                </Tooltip>
              ) : (
                <div className={styles.keyword} ref={positiveTextRef}>
                  {positiveData.text}
                </div>
              )}
              <div className={styles.badge}>
                <TrendUpIcon className={styles.trendIcon} />
                <span className={styles.percentage}>
                  {positiveData.percentage}%
                </span>
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>
        <div className={styles.divider}></div>
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dotNegative}></span>
            <span className={styles.labelText}>Most Negative</span>
          </div>
          {isLoading ? (
            <BoxSkeleton />
          ) : negativeData ? (
            <>
              {isNegativeTruncated ? (
                <Tooltip
                  overlay={negativeData.text}
                  overlayClassName="topCampaignTooltip"
                  placement="top"
                >
                  <div className={styles.keyword} ref={negativeTextRef}>
                    {negativeData.text}
                  </div>
                </Tooltip>
              ) : (
                <div className={styles.keyword} ref={negativeTextRef}>
                  {negativeData.text}
                </div>
              )}
              <div className={styles.badgeNegative}>
                <TrendUpIcon className={styles.trendIcon} />
                <span className={styles.percentage}>
                  {negativeData.percentage}%
                </span>
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>
      </div>
    </div>
  )
}
